/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import type { LucideIcon } from "lucide-react"
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react"
import type { ComponentProps } from "react"

import { pageWindow } from "../../utils/page-window"
import { Button } from "../primitives/button"
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem } from "../primitives/pagination"

interface Props extends Omit<ComponentProps<typeof Pagination>, "children"> {
    /**
     * Zero-based index of the current page.
     */
    currentPage: number
    /**
     * Total number of pages.
     *
     * The pager renders nothing when there is only one page.
     */
    totalPages: number
    /**
     * Called with the zero-based index of the selected page.
     *
     * The value is always within `0..totalPages - 1`.
     */
    onPageChange: (page: number) => void
}

/*
 * Use `Button` instead of `PaginationLink` until pages have real URLs.
 *
 * `PaginationLink` renders an anchor, but these views keep the current page in
 * their ViewModel. The device list also mirrors it to the URL with
 * `history.replaceState`, outside React Router. Without an href, the anchor
 * is not useful as a link and has no disabled state, so both navigation buttons
 * would need their own keyboard, accessibility, and disabled styling.
 *
 * #1164 will decide whether pagination gets router URLs. If it does, the page
 * buttons can switch to `PaginationLink` here; the views still own the fetch and
 * URL synchronization. The previous and next buttons remain buttons because
 * they represent actions rather than fixed destinations.
 */

/**
 * Pagination for a list whose current page is held by a ViewModel.
 *
 * Shows the first and last page, plus one page on either side of the current
 * page. Consecutive omitted pages are represented by one ellipsis.
 *
 * All other `<nav>` props are passed to the underlying pagination primitive,
 * including `aria-label` for distinguishing multiple pagers on the same page.
 *
 * @example
 * <PaginationControls
 *     currentPage={listVM.currentPage}
 *     totalPages={listVM.totalPages}
 *     onPageChange={(page) => listVM.setPage(page)}
 *     className="mt-6"
 *     aria-label="pagination, above the users"
 * />
 */
export const PaginationControls = ({ currentPage, totalPages, onPageChange, ...props }: Props) => {
    if (totalPages <= 1) return null

    return (
        <Pagination {...props}>
            <PaginationContent>
                <StepButton
                    icon={ChevronLeftIcon}
                    label="Go to previous page"
                    disabled={currentPage === 0}
                    onClick={() => onPageChange(currentPage - 1)}
                />

                {pageWindow(currentPage, totalPages).map((slot) => (
                    <PaginationItem key={slot.key}>
                        {slot.kind === "gap" ? (
                            <PaginationEllipsis />
                        ) : (
                            <PageButton
                                page={slot.page}
                                isCurrent={slot.page === currentPage}
                                onSelect={onPageChange}
                            />
                        )}
                    </PaginationItem>
                ))}

                <StepButton
                    icon={ChevronRightIcon}
                    label="Go to next page"
                    disabled={currentPage === totalPages - 1}
                    onClick={() => onPageChange(currentPage + 1)}
                />
            </PaginationContent>
        </Pagination>
    )
}

interface PageButtonProps {
    /**
     * Zero-based index of the page this button selects.
     */
    page: number
    /**
     * Whether this is the page currently being shown.
     */
    isCurrent: boolean
    /**
     * Called with the selected page's zero-based index.
     */
    onSelect: (page: number) => void
}

/**
 * Button for selecting a specific page.
 *
 * Uses a minimum width instead of a fixed icon size so multi-digit page numbers
 * have room to grow.
 */
const PageButton = ({ page, isCurrent, onSelect }: PageButtonProps) => (
    <Button
        variant={isCurrent ? "outline" : "ghost"}
        className="min-w-9"
        aria-current={isCurrent ? "page" : undefined}
        onClick={() => onSelect(page)}
    >
        {page + 1}
    </Button>
)

interface StepButtonProps {
    /**
     * Icon indicating the direction of the step.
     */
    icon: LucideIcon
    /**
     * Accessible name for the icon-only button.
     */
    label: string
    /**
     * Whether the step would move past the first or last page.
     */
    disabled: boolean
    /**
     * Called when the user selects the adjacent page.
     */
    onClick: () => void
}

/**
 * Button for moving to the adjacent page.
 *
 * Disabling it at either end prevents out-of-range page indexes from reaching
 * `onPageChange` and removes the unavailable action from the tab order.
 */
const StepButton = ({ icon: Icon, label, disabled, onClick }: StepButtonProps) => (
    <PaginationItem>
        <Button variant="ghost" size="icon" aria-label={label} disabled={disabled} onClick={onClick}>
            <Icon />
        </Button>
    </PaginationItem>
)
